// v0.20.16 — in-app browser gate.
//
// Embedded webviews (Instagram, Facebook, TikTok, …) block the camera AND are
// unreliable for passkeys/WebAuthn — which is M's entire auth. So if we detect
// one at boot, we stop before login and tell the user to open in a real browser.
//
// 1.4.45–1.4.46 (Alexander, 19 Oct) — Snapchat opens its own browser WITHOUT a link's `#` part,
// and every way it hands a link on (Open in browser, Copy link, Share via…) drops it too; only
// "Copy Link" on the message in the chat keeps the whole link. A cash note's secret used to ride
// after `#`, so the take stopped. Since 1.4.46 the secret is a path segment (/B0000000ZB/<secret>),
// which Snapchat keeps: this page then has it, and the chat app's own "open in browser" carries it.
//
// 1.4.48 — a note's or an invite's link in a browser the user agent names doesn't reach this page:
// the Pages middleware answers those with a fast gate of its own (functions/_lib/fastGate.ts).
//
// 1.4.51–1.4.52 (Alexander, 20 Oct) — Telegram's browser announces itself as Safari, version and all, down
// to a toolbar drawn like Safari's; it is Telegram's own WKWebView ("Show Instant View" in its ⋯ menu gives
// it away), and his video shows its passkey refused at once, no Face ID sheet. No user agent tells it
// apart; what does is what it can't do. 1.4.51 tried Apple Pay on the web, which Apple gives webviews too.
// 1.4.52: service workers — a WKWebView gets them only when its app holds the browser entitlement (Chrome,
// Firefox, Edge, the SFSafariViewController some apps embed) or lists the site as app-bound, which no
// messenger can do for the web at large; Safari always has them. So on an iPhone, no service workers means
// an app's own webview — this page, then, with the note's face and the one line for Telegram ("Tap ◎
// below to get it."): a browser on an iPhone that says it is Safari and isn't is Telegram's, the only app
// we know that dresses up that completely. And underneath, the certainty: when a passkey step on the take
// page is refused at once (webviewRefusal), the take page becomes this gate. /whoami lists what a browser
// can do, for the next messenger.
// The page itself: the face, "You've been sent $1.41.", the line. Nothing else (gateCopy.ts).

import { LOGO_M } from "./logo";
import { isPasskeyCancel } from "./diag";
import { COPIED, NOT_COPIED, TAKEN_HEADLINE, gateAppFromUa, gateCopyLine, gateHeadline, gateLine, isInAppUa, type GateApp, type GateGoal } from "./gateCopy";
import { backingLine } from "./fx";
import { formatNoteAmount } from "./noteCurrency"; // 1.4.54
import { noteSource } from "./notes/notes";
import { SERIES, formatSerial, type Serial } from "./notes/serial";

const onIPhone = () => /iPhone|iPad|iPod/.test(navigator.userAgent || "");

/** An iPhone without service workers: an app's webview, whatever its user agent says (1.4.52). */
function lacksBrowserEntitlement(): boolean {
  return onIPhone() && !navigator.serviceWorker;
}

/** 1.4.52 — a passkey step refused at once (NotAllowedError within two seconds: no sheet was ever shown — a real cancel can't happen faster) on an
 *  iPhone: the webview said no, not the person. `t0` is performance.now() before the step. */
export function webviewRefusal(e: unknown, t0: number): boolean {
  return onIPhone() && isPasskeyCancel(e) && performance.now() - t0 < 2000;
}

/**
 * Heuristic in-app webview detection. Conservative: matches known in-app hosts
 * and Android system WebViews, and never flags real Safari/Chrome/Firefox.
 * (1.4.48: the list lives in gateCopy.ts, shared with the Pages middleware.)
 */
export function isInAppBrowser(): boolean {
  return isInAppUa(navigator.userAgent || "") || lacksBrowserEntitlement();
}

/** The app this gate is in: by name, or Telegram by the entitlement (the only unnamed webview we know). */
export function gateApp(): GateApp {
  const byName = gateAppFromUa(navigator.userAgent || "");
  if (byName !== "other") return byName;
  return lacksBrowserEntitlement() ? "telegram" : "other";
}

export interface GateOpts {
  kind: "note" | "invite" | "drop" | "claimed" | "plain";
  /** A note's link: the face is read from Arc and the headline says the amount. */
  serial?: Serial | null;
  /** 1.4.52 — the app, when the caller knows better than the user agent (the take page's passkey refusal). */
  app?: GateApp;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
}

export function renderInAppGate(root: HTMLElement, opts: GateOpts = { kind: "plain" }): void {
  const href = window.location.href;
  const app = opts.app ?? gateApp();
  const goal: GateGoal = opts.kind === "invite" ? "join" : opts.kind === "note" || opts.kind === "drop" ? "get it" : "continue";
  root.innerHTML = `
    <main class="screen welcome gate">
      <div class="brand">${LOGO_M}</div>
      <div id="gateFace"></div>
      <h1 id="gateH1">${esc(gateHeadline(opts.kind))}</h1>
      <p class="sub">${gateLine(app, goal)}</p>
      ${app === "other" ? `<p class="sub gate-copy">${gateCopyLine()}</p>` : ""}
      <p id="gateStatus" class="status" role="status" aria-live="polite"></p>
    </main>
  `;
  const btn = root.querySelector<HTMLAnchorElement>("#copyMLink");
  const status = root.querySelector<HTMLElement>("#gateStatus")!;
  btn?.addEventListener("click", async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(href);
      status.textContent = COPIED;
    } catch {
      status.textContent = NOT_COPIED;
    }
  });
  // 1.4.51 — the note's face, once Arc answers (the take page's own markup and styles).
  const serial = opts.serial;
  if (opts.kind === "note" && serial) {
    void noteSource().bySerial(serial).then((n) => {
      if (!n || !root.contains(status)) return;
      const amount = formatNoteAmount(n.currency, n.value);
      const face = root.querySelector<HTMLElement>("#gateFace");
      const h1 = root.querySelector<HTMLElement>("#gateH1");
      if (face) face.innerHTML = `
        <div class="mnote-sm nt-note gate-note" aria-label="M® Note, ${esc(amount)}, serial ${esc(serial.text)}">
          <div class="mn-top"><span class="mn-den"><span class="mn-t">M<sup class="mn-r">®</sup> NOTE</span><small class="mn-back">${esc(backingLine(SERIES[serial.series]?.currency ?? n.currency))}</small></span><span class="mn-amt">${esc(amount)}</span></div>
          <div class="mn-meta"><span class="mn-serial">${esc(formatSerial(serial))}</span><span>${n.status === "live" ? "Not taken" : "Taken"}</span></div>
        </div>`;
      root.querySelector("main.gate")?.classList.add("has-face");
      if (h1 && n.status === "live") h1.textContent = gateHeadline("note", amount);
      else if (h1) { // 1.4.78 (Alexander, 22 Oct) — taken: the headline alone, the lines under it go (nothing to get)
        h1.textContent = TAKEN_HEADLINE;
        root.querySelectorAll<HTMLElement>("main.gate .sub").forEach((el) => el.remove());
      }
    }).catch(() => {});
  }
}
